import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import MarkdownReader from "@/components/affiliate/MarkdownReader";
import ComplianceGate from "@/components/affiliate/ComplianceGate";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Playbook reader. Renders the markdown doc with a sticky table-of-
// contents (extracted from H2 headings client-side) on desktop, a
// search-within input, and a print button.
//
// API contract:
//   GET /affiliate/content/playbooks/{slug} →
//     { slug, title, programName, body_md, lastUpdatedAt }
export default function AssetsPlaybookReader() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await affGet(`/affiliate/content/playbook/${slug}`, { auth: true });
        // The BE wraps the row in different shapes across endpoints —
        // sometimes `{items: [...]}` (foundational pattern), sometimes
        // `{item: {...}}`, sometimes `{playbook: {...}}`, `{content: {...}}`,
        // `{data: {...}}`, `{result: {...}}`, or bare. Try every shape
        // we've seen across the affiliate API.
        let piece =
          (Array.isArray(res?.items) && res.items[0]) ||
          (Array.isArray(res?.content) && res.content[0]) ||
          (Array.isArray(res?.data) && res.data[0]) ||
          (Array.isArray(res) && res[0]) ||
          res?.item ||
          res?.playbook ||
          res?.content ||
          res?.data ||
          res?.result ||
          res?.response ||
          res ||
          {};
        // If we landed on a wrapper that still doesn't have a title /
        // body, drill one level deeper in case the BE double-wraps.
        if (piece && !piece.title && !piece.body && !piece.body_md) {
          const nested = piece.item || piece.playbook || piece.content || piece.data;
          if (nested && (nested.title || nested.body || nested.body_md)) piece = nested;
        }
        // Last-resort body discovery — walk the entire response tree for
        // any long-ish string that looks like markdown (has `# ` headings
        // or `##` sections). This guarantees we render *something* even
        // if the BE renames `body` again in a future migration.
        let body_md =
          piece.body ||
          piece.body_md ||
          piece.markdown ||
          piece.bodyMarkdown ||
          piece.bodyMd ||
          piece.content_md ||
          piece.markdown_content ||
          piece.rendered_body ||
          (typeof piece.content === "string" ? piece.content : "") ||
          "";
        if (!body_md) {
          body_md = findMarkdownInTree(res);
        }
        // eslint-disable-next-line no-console
        console.debug("[playbook reader] response", { topKeys: Object.keys(res || {}), pieceKeys: Object.keys(piece || {}), foundBodyLen: body_md.length, pieceSample: piece });
        if (!cancelled) {
          setDoc({
            slug,
            title: piece.title || piece.name || piece.heading || "Playbook",
            programName: piece.programName || piece.metadata?.programName || piece.programSlug,
            body_md,
            lastUpdatedAt: piece.lastEditedByAdminAt || piece.publishedAt || piece.lastUpdatedAt || piece.updatedAt,
            // Compliance gate plumbing — BE may pass either an explicit
            // flag, an explicit compliance block, or neither (in which
            // case ComplianceGate falls back to a known-slug check +
            // Part 1 heuristic).
            requiresAcknowledgment: piece.requiresAcknowledgment === true || piece.metadata?.requiresAcknowledgment === true,
            complianceWarning: piece.complianceWarning || piece.metadata?.complianceWarning,
          });
        }
      } catch (err) {
        if (!cancelled) setDoc({ __error: err?.detail || "Not found" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Extract H2 headings from the raw markdown for the table of contents.
  // We do this client-side so the BE doesn't need to ship a structured
  // outline alongside the body. `slugify` mirrors react-markdown's
  // default anchor generation closely enough for jump-links to work
  // — we set explicit ids on each H2 below to guarantee a match.
  const toc = useMemo(() => {
    if (!doc?.body_md) return [];
    const lines = doc.body_md.split("\n");
    const out = [];
    for (const line of lines) {
      const m = line.match(/^##\s+(.+)$/);
      if (m) {
        const title = m[1].trim().replace(/[*_`]/g, "");
        out.push({ title, slug: slugify(title) });
      }
    }
    return out;
  }, [doc?.body_md]);

  // Filter the visible body by the search query — match by paragraph,
  // surrounding context kept so the result still reads naturally.
  const filteredMd = useMemo(() => {
    if (!doc?.body_md) return "";
    const q = search.trim().toLowerCase();
    if (!q) return doc.body_md;
    const sections = doc.body_md.split(/\n(?=##\s)/);
    return sections
      .filter((s) => s.toLowerCase().includes(q))
      .join("\n\n") || `_No matches for "${search}" in this playbook._`;
  }, [doc?.body_md, search]);

  if (loading) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }
  if (doc?.__error) {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "56px 24px" }}>
        <Eyebrow color={C.peachDeep}>★ NOT FOUND</Eyebrow>
        <Headline before="THIS PLAYBOOK" italic="isn't ready." size={40} className="mt-4" />
        <p style={{ marginTop: 16, fontSize: 14, color: C.warmGray }}>
          We couldn't load this playbook. It may not be published yet, or the link is incorrect.
        </p>
        <Link to="/affiliates/dashboard/assets/playbooks" style={{ display: "inline-block", marginTop: 18, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.ink }}>
          ← BACK TO PLAYBOOKS
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`playbook-${slug}`} style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px 64px" }}>
      <ComplianceGate doc={doc} slug={slug} />
      <Link
        to="/affiliates/dashboard/assets/playbooks"
        style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, textDecoration: "none" }}
      >
        ← ALL PLAYBOOKS
      </Link>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 32, marginTop: 18 }} className="md:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="hidden md:block">
          <PlaybookSidebar toc={toc} doc={doc} search={search} setSearch={setSearch} />
        </aside>
        <div ref={bodyRef}>
          {doc.programName && (
            <Eyebrow color={C.peachDeep}>★ {doc.programName.toUpperCase()}</Eyebrow>
          )}
          <h1 style={{ fontSize: 44, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", marginTop: 8, marginBottom: 6 }}>
            {doc.title}
          </h1>
          {doc.lastUpdatedAt && (
            <div style={{ fontSize: 11, color: C.warmGray, fontWeight: 700, letterSpacing: "0.08em" }}>
              UPDATED {new Date(doc.lastUpdatedAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
            </div>
          )}
          <div className="md:hidden" style={{ marginTop: 18 }}>
            <PlaybookSearch search={search} setSearch={setSearch} />
          </div>
          <div style={{ marginTop: 32 }}>
            <MarkdownReader source={filteredMd} testId="playbook-body" programSlug={slug} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PlaybookSidebar({ toc, doc, search, setSearch }) {
  return (
    <div style={{ position: "sticky", top: 110, display: "flex", flexDirection: "column", gap: 16 }}>
      <PlaybookSearch search={search} setSearch={setSearch} />
      {toc.length > 0 && (
        <div data-testid="playbook-toc" style={{ background: C.bone, padding: 18 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray, marginBottom: 12 }}>
            ★ ON THIS PAGE
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {toc.map((t) => (
              <li key={t.slug}>
                <a
                  href={`#${t.slug}`}
                  data-testid={`playbook-toc-${t.slug}`}
                  style={{ fontSize: 13, color: C.ink, textDecoration: "none", lineHeight: 1.45, display: "block" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = C.peachDeep; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = C.ink; }}
                >
                  · {t.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={() => window.print()}
        data-testid="playbook-print"
        style={{
          padding: "12px 14px",
          background: C.ink,
          color: C.cream,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.22em",
          border: 0,
          cursor: "pointer",
        }}
      >
        PRINT / SAVE AS PDF
      </button>
    </div>
  );
}

function PlaybookSearch({ search, setSearch }) {
  return (
    <input
      type="search"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search this playbook…"
      data-testid="playbook-search"
      style={{
        width: "100%",
        padding: "12px 14px",
        fontSize: 13,
        background: C.pure,
        border: `1px solid ${C.warmGray3}`,
        color: C.ink,
        outline: "none",
      }}
    />
  );
}

function slugify(s) {
  return s.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}


// Last-resort body discovery. If the BE renames `body` again or wraps
// the markdown in a key we haven't anticipated, we still want to
// render *something* rather than show an empty card. Walks the entire
// response tree (object + array values) looking for the longest
// markdown-shaped string. Returns "" if none found.
function findMarkdownInTree(node, depth = 0) {
  if (!node || depth > 6) return "";
  // Treat any decently long string with structural markdown cues as a
  // candidate. We avoid being too aggressive (no plain prose) to keep
  // from accidentally rendering an error message or metadata blob.
  if (typeof node === "string") {
    const looksLikeMd =
      node.length > 200 &&
      /(^|\n)#{1,4}\s/.test(node); // any `#`/`##`/`###`/`####` heading
    return looksLikeMd ? node : "";
  }
  if (Array.isArray(node)) {
    let best = "";
    for (const child of node) {
      const found = findMarkdownInTree(child, depth + 1);
      if (found.length > best.length) best = found;
    }
    return best;
  }
  if (typeof node === "object") {
    let best = "";
    for (const k of Object.keys(node)) {
      const found = findMarkdownInTree(node[k], depth + 1);
      if (found.length > best.length) best = found;
    }
    return best;
  }
  return "";
}
